import { eachMonthOfInterval, format, parseISO, startOfMonth } from 'date-fns'
import { Tables } from '../../db_types'
import { isPayoutExpired } from './status'
import { isRegularManualPayout } from './payoutPredicates'
export type PayoutAnalyticsParams = {
  organizationId: number
  startDate: string
  endDate: string
  teamId?: number | null
  planId?: number | null
  tagIds?: number[] | null
}

export type PayoutAnalyticsProfile = Pick<Tables<'profiles'>, 'email' | 'id' | 'img_url' | 'name'>

export type PayoutAnalyticsStatement = Pick<Tables<'statements'>, 'period_start' | 'status'>

export type PayoutAnalyticsInstallment = Pick<
  Tables<'payout_installments'>,
  'amount' | 'id' | 'statement_id' | 'status'
> & {
  statements?: PayoutAnalyticsStatement | null
}

export type PayoutAnalyticsPayout = Pick<
  Tables<'payouts'>,
  | 'compensation_rule_id'
  | 'description'
  | 'effective_date'
  | 'id'
  | 'is_withheld'
  | 'payout_discrepancy_id'
  | 'profile_id'
  | 'statement_id'
  | 'status'
  | 'target_id'
  | 'value'
> & {
  payout_installments?: (PayoutAnalyticsInstallment | null)[] | null
  profiles?: PayoutAnalyticsProfile | null
  statements?: PayoutAnalyticsStatement | null
}

export type PayoutAnalyticsAdjustment = Pick<
  Tables<'payout_adjustments'>,
  'amount' | 'created_at' | 'id' | 'payout_id'
> & {
  comment?: string | null
  profile_id?: string | null
  profiles?: PayoutAnalyticsProfile | null
  payouts?: {
    id: number
    profile_id: string
    statement_id?: number | null
    profiles?: PayoutAnalyticsProfile | null
    statements?: PayoutAnalyticsStatement | null
  }
}

export type PayoutAnalyticsAdjustmentAudit = Pick<
  Tables<'payouts_audit_log'>,
  'changed_at' | 'new_data' | 'old_data'
> &
  Partial<Pick<Tables<'payouts_audit_log'>, 'changed_by' | 'operation_type'>> & {
    changed_by_profile?: PayoutAnalyticsProfile | null
  }

export type PayoutAnalyticsEvent = {
  actor: PayoutAnalyticsProfile | null
  amount: number
  createdAt: string
  id: number
  payoutId: number
  reason: string | null
  receiver: PayoutAnalyticsProfile | null
  statementPeriodStart: string | null
  statementStatus: PayoutAnalyticsStatement['status'] | null
}

export type PayoutAdjustmentEvent = PayoutAnalyticsEvent & {
  fromAmount: number | null
  toAmount: number
}

export type PayoutAdjustmentStep = {
  actor: PayoutAnalyticsProfile | null
  adjustmentAmount: number | null
  createdAt: string
  fromAmount: number | null
  id: number
  profile: PayoutAnalyticsProfile | null
  reason: string | null
  receiver: PayoutAnalyticsProfile | null
  statementPeriodStart: string | null
  statementStatus: PayoutAnalyticsStatement['status'] | null
  toAmount: number
}

export type PayoutAdjustmentBreakdown = {
  adjustedAmount: number
  adjustmentAmount: number | null
  createdAt: string
  originalAmount: number | null
  payoutId: number
  profile: PayoutAnalyticsProfile | null
  steps: PayoutAdjustmentStep[]
}

export type PayoutAnalyticsPerson = {
  email: string | null
  entriesCount: number
  imgUrl: string | null
  name: string | null
  profileId: string
  totalAmount: number
}

export type GeneratedPayoutBucket = {
  kind: 'expired' | 'locked' | 'statement_month'
  periodStart: string | null
  totalAmount: number
}

export type PayablePayoutMonthBucket = {
  paidAmount: number
  periodStart: string
  totalAmount: number
}

export type PayablePayoutMonthTotal = {
  paidAmount: number
  pendingAmount: number
  periodStart: string
}

/**
 * Generated-payout totals are source/installment aggregates:
 * `lockedAmount` is unassigned or withheld, and `expiredAmount` includes
 * payout-level expiry plus expired installments. They do not represent paid
 * or validated payout-level totals.
 */
export type GeneratedPayoutStatusTotals = {
  accruedAmount: number
  expiredAmount: number
  lockedAmount: number
  totalAmount: number
}

export type PayoutAnalytics = {
  generatedPayouts: {
    buckets: GeneratedPayoutBucket[]
    totalAmount: number
  }
  manualCompensationAndDeductions: {
    entriesCount: number
    events?: PayoutAnalyticsEvent[]
    people: PayoutAnalyticsPerson[]
    totalAmount: number
  }
  payoutAdjustments: {
    entriesCount: number
    events?: PayoutAdjustmentEvent[]
    people: PayoutAnalyticsPerson[]
    totalAmount: number
  }
  payoutsToBePaid: {
    buckets: PayablePayoutMonthBucket[]
    paidAmount: number
    paidPercent: number
    pendingAmount: number
    pendingPercent: number
    totalAmount: number
  }
}

export type GeneratedPayoutsAnalytics = PayoutAnalytics['generatedPayouts']
export type PayablePayoutsAnalytics = PayoutAnalytics['payoutsToBePaid']
export type ManualPayoutsAnalytics = PayoutAnalytics['manualCompensationAndDeductions']
export type PayoutAdjustmentsAnalytics = PayoutAnalytics['payoutAdjustments']

const EPSILON = 0.000001

const roundAmount = (value: number) => Math.round(value * 100) / 100

const buildPersonLookupKey = (profileId: string) => profileId

const getProfileSummary = (profileId: string, profile?: PayoutAnalyticsProfile | null) => ({
  email: profile?.email ?? null,
  imgUrl: profile?.img_url ?? null,
  name: profile?.name ?? null,
  profileId
})

const sortPeopleByAmount = (people: PayoutAnalyticsPerson[]) =>
  [...people].sort((left, right) => {
    if (right.totalAmount !== left.totalAmount) {
      return right.totalAmount - left.totalAmount
    }

    const leftLabel = left.name ?? left.email ?? left.profileId
    const rightLabel = right.name ?? right.email ?? right.profileId
    return leftLabel.localeCompare(rightLabel)
  })

const isStatementPaid = (statement?: PayoutAnalyticsStatement | null) =>
  statement?.status === 'paid'

const addAmountToBucket = (
  buckets: Map<string, GeneratedPayoutBucket>,
  key: string,
  bucket: GeneratedPayoutBucket
) => {
  const current = buckets.get(key)
  if (!current) {
    buckets.set(key, {
      ...bucket,
      totalAmount: roundAmount(bucket.totalAmount)
    })
    return
  }

  current.totalAmount = roundAmount(current.totalAmount + bucket.totalAmount)
}

const addAmountToPayoutMonthBucket = (
  buckets: Map<string, PayablePayoutMonthBucket>,
  periodStart: string,
  amount: number,
  paid: boolean
) => {
  const current = buckets.get(periodStart) ?? {
    paidAmount: 0,
    periodStart,
    totalAmount: 0
  }

  current.totalAmount = roundAmount(current.totalAmount + amount)
  if (paid) {
    current.paidAmount = roundAmount(current.paidAmount + amount)
  }

  buckets.set(periodStart, current)
}

const appendPersonAmount = (
  peopleById: Map<string, PayoutAnalyticsPerson>,
  profileId: string,
  profile: PayoutAnalyticsProfile | null | undefined,
  amount: number
) => {
  const key = buildPersonLookupKey(profileId)
  const current = peopleById.get(key) ?? {
    ...getProfileSummary(profileId, profile),
    entriesCount: 0,
    totalAmount: 0
  }

  current.entriesCount += 1
  current.totalAmount = roundAmount(current.totalAmount + amount)

  peopleById.set(key, current)
}

export const buildStatementMonthStarts = (startDate: string, endDate: string) =>
  eachMonthOfInterval({
    start: startOfMonth(parseISO(startDate)),
    end: startOfMonth(parseISO(endDate))
  }).map(date => format(date, 'yyyy-MM-dd'))

const getJsonNumber = (value: unknown, key: string) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const raw = (value as Record<string, unknown>)[key]
  const parsed = Number(raw)

  return Number.isFinite(parsed) ? parsed : null
}

export const buildGeneratedPayoutsSummary = (
  payouts: PayoutAnalyticsPayout[],
  statementMonthStarts?: string[]
): GeneratedPayoutsAnalytics => {
  const buckets = new Map<string, GeneratedPayoutBucket>()
  const selectedStatementMonths = statementMonthStarts ? new Set(statementMonthStarts) : null
  let totalAmount = 0

  payouts.forEach(payout => {
    if (selectedStatementMonths) {
      const installments =
        payout.payout_installments?.filter(installment => installment != null) ?? []

      if (installments.length > 0) {
        installments.forEach(installment => {
          const periodStart = installment?.statements?.period_start ?? null

          if (
            !installment?.statement_id ||
            !periodStart ||
            !selectedStatementMonths.has(periodStart)
          ) {
            return
          }

          totalAmount = roundAmount(totalAmount + installment.amount)
          addAmountToBucket(buckets, periodStart, {
            kind: 'statement_month',
            periodStart,
            totalAmount: installment.amount
          })
        })
        return
      }

      const periodStart = payout.statements?.period_start ?? null
      if (!payout.statement_id || !periodStart || !selectedStatementMonths.has(periodStart)) {
        return
      }

      totalAmount = roundAmount(totalAmount + payout.value)
      addAmountToBucket(buckets, periodStart, {
        kind: 'statement_month',
        periodStart,
        totalAmount: payout.value
      })
      return
    }

    totalAmount = roundAmount(totalAmount + payout.value)

    const installments =
      payout.payout_installments?.filter(installment => installment != null) ?? []

    if (installments.length > 0) {
      let distributedAmount = 0

      installments.forEach(installment => {
        if (!installment) return

        distributedAmount = roundAmount(distributedAmount + installment.amount)

        if (installment.status === 'expired') {
          addAmountToBucket(buckets, 'expired', {
            kind: 'expired',
            periodStart: null,
            totalAmount: installment.amount
          })
          return
        }

        const periodStart = installment.statements?.period_start ?? null

        if (!installment.statement_id || !periodStart) {
          addAmountToBucket(buckets, 'locked', {
            kind: 'locked',
            periodStart: null,
            totalAmount: installment.amount
          })
          return
        }

        addAmountToBucket(buckets, periodStart, {
          kind: 'statement_month',
          periodStart,
          totalAmount: installment.amount
        })
      })

      const undistributedAmount = roundAmount(payout.value - distributedAmount)
      if (Math.abs(undistributedAmount) > EPSILON) {
        if (isPayoutExpired(payout, { includeInstallments: false })) {
          addAmountToBucket(buckets, 'expired', {
            kind: 'expired',
            periodStart: null,
            totalAmount: undistributedAmount
          })
        } else if (payout.statement_id && payout.statements?.period_start) {
          addAmountToBucket(buckets, payout.statements.period_start, {
            kind: 'statement_month',
            periodStart: payout.statements.period_start,
            totalAmount: undistributedAmount
          })
        } else {
          addAmountToBucket(buckets, 'locked', {
            kind: 'locked',
            periodStart: null,
            totalAmount: undistributedAmount
          })
        }
      }

      return
    }

    if (isPayoutExpired(payout)) {
      addAmountToBucket(buckets, 'expired', {
        kind: 'expired',
        periodStart: null,
        totalAmount: payout.value
      })
      return
    }

    const periodStart = payout.statements?.period_start ?? null
    if (payout.statement_id && periodStart && payout.is_withheld !== true) {
      addAmountToBucket(buckets, periodStart, {
        kind: 'statement_month',
        periodStart,
        totalAmount: payout.value
      })
      return
    }

    addAmountToBucket(buckets, 'locked', {
      kind: 'locked',
      periodStart: null,
      totalAmount: payout.value
    })
  })

  const sortedBuckets = [...buckets.values()].sort((left, right) => {
    if (left.kind === 'statement_month' && right.kind === 'statement_month') {
      return (left.periodStart ?? '').localeCompare(right.periodStart ?? '')
    }

    if (left.kind === 'statement_month') return -1
    if (right.kind === 'statement_month') return 1
    if (left.kind === 'locked' && right.kind === 'expired') return -1
    if (left.kind === 'expired' && right.kind === 'locked') return 1
    return 0
  })

  return {
    buckets: sortedBuckets,
    totalAmount
  }
}

export const buildGeneratedPayoutsSummaryFromStatusTotals = (
  totals: GeneratedPayoutStatusTotals
): GeneratedPayoutsAnalytics => {
  const accruedAmount = roundAmount(totals.accruedAmount)
  const lockedAmount = roundAmount(totals.lockedAmount)
  const expiredAmount = roundAmount(totals.expiredAmount)
  const buckets: GeneratedPayoutBucket[] = []

  if (accruedAmount) {
    buckets.push({
      kind: 'statement_month',
      periodStart: null,
      totalAmount: accruedAmount
    })
  }

  if (lockedAmount) {
    buckets.push({
      kind: 'locked',
      periodStart: null,
      totalAmount: lockedAmount
    })
  }

  if (expiredAmount) {
    buckets.push({
      kind: 'expired',
      periodStart: null,
      totalAmount: expiredAmount
    })
  }

  return {
    buckets,
    totalAmount: roundAmount(totals.totalAmount)
  }
}

/**
 * Payable totals use statement status as the payment semantic. Installment
 * amounts use each installment's statement; non-installment amounts use the
 * payout statement. Persisted payout status alone is not treated as paid here.
 */
export const buildPayoutsToBePaidSummary = (
  payouts: PayoutAnalyticsPayout[],
  statementMonthStarts: string[]
): PayablePayoutsAnalytics => {
  const statementMonths = new Set(statementMonthStarts)
  const buckets = new Map<string, PayablePayoutMonthBucket>()
  let paidAmount = 0
  let pendingAmount = 0

  payouts.forEach(payout => {
    const installments =
      payout.payout_installments?.filter(installment => installment != null) ?? []

    if (installments.length > 0) {
      installments.forEach(installment => {
        if (!installment?.statement_id || !installment.statements?.period_start) {
          return
        }

        if (!statementMonths.has(installment.statements.period_start)) {
          return
        }

        const paid = isStatementPaid(installment.statements)
        addAmountToPayoutMonthBucket(
          buckets,
          installment.statements.period_start,
          installment.amount,
          paid
        )

        if (paid) {
          paidAmount = roundAmount(paidAmount + installment.amount)
        } else {
          pendingAmount = roundAmount(pendingAmount + installment.amount)
        }
      })

      return
    }

    if (!payout.statement_id || !payout.statements?.period_start) {
      return
    }

    if (!statementMonths.has(payout.statements.period_start)) {
      return
    }

    const paid = isStatementPaid(payout.statements)
    addAmountToPayoutMonthBucket(buckets, payout.statements.period_start, payout.value, paid)

    if (paid) {
      paidAmount = roundAmount(paidAmount + payout.value)
    } else {
      pendingAmount = roundAmount(pendingAmount + payout.value)
    }
  })

  const totalAmount = roundAmount(paidAmount + pendingAmount)

  return {
    buckets: [...buckets.values()].sort((left, right) =>
      left.periodStart.localeCompare(right.periodStart)
    ),
    paidAmount,
    paidPercent: totalAmount > 0 ? paidAmount / totalAmount : 0,
    pendingAmount,
    pendingPercent: totalAmount > 0 ? pendingAmount / totalAmount : 0,
    totalAmount
  }
}

export const buildPayoutsToBePaidSummaryFromMonthTotals = (
  monthTotals: PayablePayoutMonthTotal[]
): PayablePayoutsAnalytics => {
  const buckets = monthTotals
    .map(month => ({
      paidAmount: roundAmount(month.paidAmount),
      periodStart: month.periodStart,
      totalAmount: roundAmount(month.paidAmount + month.pendingAmount)
    }))
    .sort((left, right) => left.periodStart.localeCompare(right.periodStart))
  const paidAmount = roundAmount(monthTotals.reduce((total, month) => total + month.paidAmount, 0))
  const pendingAmount = roundAmount(
    monthTotals.reduce((total, month) => total + month.pendingAmount, 0)
  )
  const totalAmount = roundAmount(paidAmount + pendingAmount)

  return {
    buckets,
    paidAmount,
    paidPercent: totalAmount > 0 ? paidAmount / totalAmount : 0,
    pendingAmount,
    pendingPercent: totalAmount > 0 ? pendingAmount / totalAmount : 0,
    totalAmount
  }
}

export const buildManualCompensationSummary = (
  payouts: PayoutAnalyticsPayout[],
  auditRows: PayoutAnalyticsAdjustmentAudit[] = []
): ManualPayoutsAnalytics => {
  const peopleById = new Map<string, PayoutAnalyticsPerson>()
  const auditRowsByPayoutId = new Map<number, PayoutAnalyticsAdjustmentAudit>()
  let totalAmount = 0
  let entriesCount = 0
  const events: PayoutAnalyticsEvent[] = []

  auditRows.forEach(row => {
    if (row.operation_type !== 'INSERT') return

    const payoutId = getJsonNumber(row.new_data, 'id')
    if (payoutId != null) auditRowsByPayoutId.set(payoutId, row)
  })

  payouts.forEach(payout => {
    if (!isRegularManualPayout(payout)) return

    totalAmount = roundAmount(totalAmount + payout.value)
    entriesCount += 1
    const auditRow = auditRowsByPayoutId.get(payout.id)
    events.push({
      actor: auditRow?.changed_by_profile ?? null,
      amount: payout.value,
      createdAt: auditRow?.changed_at ?? payout.effective_date,
      id: payout.id,
      payoutId: payout.id,
      reason: payout.description ?? null,
      receiver: payout.profiles ?? null,
      statementPeriodStart: payout.statements?.period_start ?? null,
      statementStatus: payout.statements?.status ?? null
    })
    appendPersonAmount(peopleById, payout.profile_id, payout.profiles, payout.value)
  })

  return {
    entriesCount,
    events,
    people: sortPeopleByAmount([...peopleById.values()]),
    totalAmount
  }
}

export const buildPayoutAdjustmentsSummary = (
  adjustments: PayoutAnalyticsAdjustment[],
  auditRows: PayoutAnalyticsAdjustmentAudit[]
): PayoutAdjustmentsAnalytics => {
  const breakdowns = buildPayoutAdjustmentBreakdowns(adjustments, auditRows)
  const peopleById = new Map<string, PayoutAnalyticsPerson>()
  const events: PayoutAdjustmentEvent[] = []
  let totalAmount = 0
  let entriesCount = 0

  breakdowns.forEach(breakdown => {
    breakdown.steps.forEach(step => {
      entriesCount += 1

      events.push({
        actor: step.actor,
        amount: step.adjustmentAmount ?? 0,
        createdAt: step.createdAt,
        fromAmount: step.fromAmount,
        id: step.id,
        payoutId: breakdown.payoutId,
        reason: step.reason,
        receiver: step.receiver,
        statementPeriodStart: step.statementPeriodStart,
        statementStatus: step.statementStatus,
        toAmount: step.toAmount
      })

      if (step.adjustmentAmount == null) return

      totalAmount = roundAmount(totalAmount + step.adjustmentAmount)

      const profileId = breakdown.profile?.id
      if (!profileId) return

      appendPersonAmount(peopleById, profileId, breakdown.profile, step.adjustmentAmount)
    })
  })

  return {
    entriesCount,
    events,
    people: sortPeopleByAmount([...peopleById.values()]),
    totalAmount
  }
}

export const buildPayoutAdjustmentBreakdowns = (
  adjustments: PayoutAnalyticsAdjustment[],
  auditRows: PayoutAnalyticsAdjustmentAudit[]
): PayoutAdjustmentBreakdown[] => {
  const auditRowsByCompositeKey = new Map<string, PayoutAnalyticsAdjustmentAudit>()
  const adjustmentsByPayout = new Map<number, PayoutAnalyticsAdjustment[]>()

  auditRows.forEach(row => {
    const payoutId = getJsonNumber(row.old_data, 'id')
    const newValue = getJsonNumber(row.new_data, 'value')

    if (payoutId == null || newValue == null) return

    auditRowsByCompositeKey.set(`${payoutId}:${row.changed_at}:${newValue}`, row)
  })

  adjustments.forEach(adjustment => {
    const rows = adjustmentsByPayout.get(adjustment.payout_id) ?? []
    rows.push(adjustment)
    adjustmentsByPayout.set(adjustment.payout_id, rows)
  })

  return Array.from(adjustmentsByPayout.entries()).map(([payoutId, rows]) => {
    const orderedRows = [...rows].sort((left, right) => {
      if (left.created_at !== right.created_at) {
        return left.created_at.localeCompare(right.created_at)
      }

      return left.id - right.id
    })

    let previousValue: number | null = null

    const steps = orderedRows.map(adjustment => {
      const auditRow = auditRowsByCompositeKey.get(
        `${adjustment.payout_id}:${adjustment.created_at}:${adjustment.amount}`
      )
      const fromValue = getJsonNumber(auditRow?.old_data, 'value') ?? previousValue
      const adjustmentAmount = fromValue == null ? 0 : roundAmount(adjustment.amount - fromValue)

      previousValue = adjustment.amount

      return {
        actor: adjustment.profiles ?? null,
        adjustmentAmount: fromValue == null ? null : adjustmentAmount,
        createdAt: adjustment.created_at,
        fromAmount: fromValue,
        id: adjustment.id,
        profile: adjustment.payouts?.profiles ?? null,
        reason: adjustment.comment ?? null,
        receiver: adjustment.payouts?.profiles ?? null,
        statementPeriodStart: adjustment.payouts?.statements?.period_start ?? null,
        statementStatus: adjustment.payouts?.statements?.status ?? null,
        toAmount: adjustment.amount
      } satisfies PayoutAdjustmentStep
    })

    const firstStep = steps[0] ?? null
    const latestStep = steps[steps.length - 1] ?? null
    const originalAmount = firstStep?.fromAmount ?? null
    const adjustedAmount = latestStep?.toAmount ?? 0

    return {
      adjustedAmount,
      adjustmentAmount:
        originalAmount == null ? null : roundAmount(adjustedAmount - originalAmount),
      createdAt: latestStep?.createdAt ?? '',
      originalAmount,
      payoutId,
      profile: orderedRows[orderedRows.length - 1]?.payouts?.profiles ?? null,
      steps
    } satisfies PayoutAdjustmentBreakdown
  })
}
